import React, { useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const MIN_LENGTH = 8;

export default function ChangePasswordForm() {
  const { user, isDemo, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const demoAccount = isDemo || user?.demo;

  async function handleSubmit(e) {
    e.preventDefault();

    if (newPassword.length < MIN_LENGTH) {
      toast.error(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must be different from your current password.");
      return;
    }

    setSubmitting(true);
    const result = await changePassword(currentPassword, newPassword);
    setSubmitting(false);

    if (result.ok) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password updated successfully.");
    } else {
      toast.error(result.error || "Could not update password.");
    }
  }

  return (
    <section
      className="bg-white rounded-xl border border-gray-200 shadow-card p-6 max-w-3xl"
      data-testid="admin-change-password-section"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-cream flex items-center justify-center shrink-0">
          <Lock size={18} className="text-maroon" />
        </div>
        <div className="min-w-0">
          <h2 className="font-serif text-xl font-semibold text-ink">Change password</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Update the password for <span className="font-medium text-ink">{user?.email}</span>.
          </p>
        </div>
      </div>

      {demoAccount ? (
        <p className="mt-5 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="change-password-demo-notice">
          Password changes are not available in demo mode. Sign in with your Supabase admin account to
          manage your password.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4 max-w-md">
          <div>
            <label className="label-bcs" htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              type="password"
              className="input-bcs"
              required
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="change-password-current"
            />
          </div>
          <div>
            <label className="label-bcs" htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              className="input-bcs"
              required
              minLength={MIN_LENGTH}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="change-password-new"
            />
          </div>
          <div>
            <label className="label-bcs" htmlFor="confirm-password">Confirm new password</label>
            <input
              id="confirm-password"
              type="password"
              className="input-bcs"
              required
              minLength={MIN_LENGTH}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="change-password-confirm"
            />
          </div>
          <p className="text-xs text-ink-muted">Use at least {MIN_LENGTH} characters.</p>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
            data-testid="change-password-submit"
          >
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </section>
  );
}
